import React, { useState, useEffect } from 'react';
import { Search, Loader2, AlertCircle, Car, User, Shield, FileText, List, Download, RotateCcw } from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import BulkAstsPublicInput from '../components/BulkAstsPublicInput';
import BulkAstsPublicResults from '../components/BulkAstsPublicResults';
import AstsPublicLienholders, { AstsPublicLienFields } from '../components/AstsPublicLienholders';
import { useAuth } from '../context/AuthContext';
import { makeAuthenticatedRequest, getErrorMessage } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import { formatMvrDate } from '../services/mvrService';
import {
  BulkAstsPublicItem,
  BulkAstsPublicResponse,
  submitBulkAstsPublic,
  generateAstsPublicPdf,
  localizeRateLimitMessage,
} from '../services/astsPublicService';

interface PublicState {
  code: string;
  name: string;
}

interface TitleRecord extends AstsPublicLienFields {
  vin?: string;
  titleNumber?: string;
  titleStatus?: string;
  titleDate?: string;
  titleType?: string;
  titleFormat?: string;
  controlNumber?: string;
  purchasePrice?: number;
  year?: number;
  make?: string;
  model?: string;
  bodyStyle?: string;
  color?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  ownerCounty?: string;
  odometerReading?: number;
  odometerStatus?: string;
  brand?: string;
}

interface RegistrationRecord {
  vin?: string;
  plate?: string;
  plateType?: string;
  registrationStatus?: string;
  registrationDate?: string;
  expirationDate?: string;
  year?: number;
  make?: string;
  model?: string;
  color?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  county?: string;
}

interface SearchResult {
  success: boolean;
  errorMessage?: string;
  errorCode?: string;
  /** While the state is rate-limiting our searches: when they resume (UTC, ISO). */
  availableAgainAtUtc?: string;
  state?: string;
  titleResponse?: {
    success: boolean;
    titles?: TitleRecord[];
  };
  registrationResponse?: {
    success: boolean;
    registrations?: RegistrationRecord[];
  };
}

const DataField: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <div className="text-xs text-[#64748b] mb-0.5">{label}</div>
      <div className="font-medium text-[#0f172a]">{value}</div>
    </div>
  );
};

const AstsPublicInquiry: React.FC = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [publicStates, setPublicStates] = useState<PublicState[]>([]);
  const [selectedState, setSelectedState] = useState('');
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pdfLoading, setPdfLoading] = useState(false);

  // Bulk mode state
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [bulkResults, setBulkResults] = useState<BulkAstsPublicResponse | null>(null);
  const [isBulkSearching, setIsBulkSearching] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await makeAuthenticatedRequest(API_ENDPOINTS.ASTS_PUBLIC.STATES);
        if (response.ok) {
          const data = await response.json();
          setPublicStates(data.states ?? []);
          if (data.states?.length === 1) {
            setSelectedState(data.states[0].code);
          }
        }
      } catch {
        // States list failure is non-critical
      }
    };
    fetchStates();
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedState || !vin.trim()) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const url = `${API_ENDPOINTS.ASTS_PUBLIC.SEARCH(selectedState)}?vin=${encodeURIComponent(vin.trim().toUpperCase())}`;
      const response = await makeAuthenticatedRequest(url);
      const data: SearchResult = await response.json();

      if (response.status === 403) {
        setError('You do not have access to ASTS Public Inquiry.');
        return;
      }
      if (response.status === 404) {
        setError(data.errorMessage ?? 'State not available for public inquiry.');
        return;
      }

      // A failed lookup is not "no records": show why it failed (portal down, rate-limited, timed out).
      if (!data.success && data.errorMessage) {
        setError(localizeRateLimitMessage(data.errorMessage, data.availableAgainAtUtc));
        return;
      }

      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSearch = async (items: BulkAstsPublicItem[]) => {
    setIsBulkSearching(true);
    setBulkResults(null);
    setBulkError(null);

    try {
      const response = await submitBulkAstsPublic(items);
      setBulkResults(response);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to process bulk request');
    } finally {
      setIsBulkSearching(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedState || !vin.trim() || !result?.titleResponse) return;
    setPdfLoading(true);
    try {
      await generateAstsPublicPdf(
        selectedState,
        vin.trim().toUpperCase(),
        result.titleResponse,
      );
    } catch {
      // silently fail — user can retry
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedState(publicStates.length === 1 ? publicStates[0].code : '');
    setVin('');
    setResult(null);
    setError(null);
  };

  const hasNoAccess = user?.canAccessAstsPublic === false;

  const titles = result?.titleResponse?.titles ?? [];
  const hasResults = titles.length > 0;

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      <Navigation onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex flex-1 pt-[88px]">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 lg:ml-[240px] p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-[#c41e3a]/10 rounded-lg">
                <Search className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f172a]">ASTS Public Inquiry</h1>
                <p className="text-sm text-[#64748b]">Free public DMV record lookup</p>
              </div>
            </div>

            {hasNoAccess ? (
              <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-8 text-center">
                <Shield className="w-12 h-12 text-[#94a3b8] mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-[#0f172a] mb-2">Access Restricted</h2>
                <p className="text-[#64748b]">You do not have permission to use ASTS Public Inquiry.</p>
              </div>
            ) : (
              <>
                {/* Mode Toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setMode('single')}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      mode === 'single'
                        ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
                        : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    Single Lookup
                  </button>
                  <button
                    onClick={() => setMode('bulk')}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      mode === 'bulk'
                        ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
                        : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    Bulk Lookup
                  </button>
                </div>

                {/* Single Lookup Mode */}
                {mode === 'single' && (
                  <>
                    {/* Search Form */}
                    <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-6 mb-6">
                      <form onSubmit={handleSearch} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-[#374151] mb-1.5">State</label>
                            <select
                              value={selectedState}
                              onChange={(e) => setSelectedState(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                              required
                            >
                              <option value="">Select a state...</option>
                              {publicStates.map((s) => (
                                <option key={s.code} value={s.code}>{s.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#374151] mb-1.5">VIN</label>
                            <input
                              type="text"
                              value={vin}
                              onChange={(e) => setVin(e.target.value.toUpperCase())}
                              placeholder="Enter 17-character VIN"
                              maxLength={17}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                              required
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={handleClear}
                            disabled={loading}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#64748b] border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 hover:text-[#0f172a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <RotateCcw className="w-4 h-4" /> Clear
                          </button>
                          <button
                            type="submit"
                            disabled={loading || !selectedState || !vin.trim()}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[#c41e3a] text-white rounded-lg text-sm font-medium hover:bg-[#a01830] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {loading ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
                            ) : (
                              <><Search className="w-4 h-4" /> Search</>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}

                    {/* No results */}
                    {result && !hasResults && !error && (
                      <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-8 text-center">
                        <Search className="w-10 h-10 text-[#94a3b8] mx-auto mb-3" />
                        <p className="text-[#64748b]">No records found for this VIN in {selectedState}.</p>
                      </div>
                    )}

                    {/* Title Records */}
                    {titles.length > 0 && (
                      <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm overflow-hidden mb-4">
                        <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200">
                          <FileText className="w-5 h-5 text-[#64748b]" />
                          <span className="font-medium text-[#0f172a]">Title Records</span>
                          <span className="text-[#64748b]">({titles.length})</span>
                          <div className="ml-auto">
                            <button
                              onClick={handleDownloadPdf}
                              disabled={pdfLoading}
                              className="flex items-center gap-2 px-4 py-1.5 bg-[#c41e3a] text-white rounded-lg text-sm font-medium hover:bg-[#a01830] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {pdfLoading ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
                              ) : (
                                <><Download className="w-3.5 h-3.5" /> Generate PDF</>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {titles.map((title, i) => (
                            <div key={i} className="p-5 space-y-4">
                              {/* Vehicle */}
                              <div>
                                <div className="flex items-center gap-2 mb-3">
                                  <Car className="w-4 h-4 text-[#64748b]" />
                                  <span className="text-sm font-semibold text-[#374151]">Vehicle</span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <DataField label="VIN" value={title.vin} />
                                  <DataField label="Year" value={title.year} />
                                  <DataField label="Make" value={title.make} />
                                  <DataField label="Model" value={title.model} />
                                  <DataField label="Body Style" value={title.bodyStyle} />
                                  <DataField label="Color" value={title.color} />
                                  <DataField label="Odometer" value={title.odometerReading != null ? `${title.odometerReading.toLocaleString()} mi` : null} />
                                  <DataField label="Odometer Status" value={title.odometerStatus} />
                                </div>
                              </div>
                              {/* Title Info */}
                              <div>
                                <div className="flex items-center gap-2 mb-3">
                                  <FileText className="w-4 h-4 text-[#64748b]" />
                                  <span className="text-sm font-semibold text-[#374151]">Title</span>
                                  {title.titleFormat && (
                                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                                      title.titleFormat.toLowerCase() === 'paper'
                                        ? 'bg-orange-100 text-orange-700'
                                        : title.titleFormat.toLowerCase() === 'electronic'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-[#f1f5f9] text-[#64748b]'
                                    }`}>{title.titleFormat}</span>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <DataField label="Title Number" value={title.titleNumber} />
                                  <DataField label="Control Number" value={title.controlNumber} />
                                  <DataField label="Title Status" value={title.titleStatus} />
                                  <DataField label="Title Date" value={formatMvrDate(title.titleDate)} />
                                  <DataField label="Title Type" value={title.titleType} />
                                  <DataField label="Purchase Price" value={title.purchasePrice != null ? `$${title.purchasePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} />
                                  {title.ownerCounty && <DataField label="County of Residence" value={title.ownerCounty} />}
                                  {title.brand && <DataField label="Brand" value={title.brand} />}
                                </div>
                              </div>
                              {/* Owner */}
                              {title.ownerName && (
                                <div>
                                  <div className="flex items-center gap-2 mb-3">
                                    <User className="w-4 h-4 text-[#64748b]" />
                                    <span className="text-sm font-semibold text-[#374151]">Owner</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="col-span-2">
                                      <DataField label="Name" value={title.ownerName} />
                                    </div>
                                    <DataField label="City" value={title.ownerCity} />
                                    <DataField label="State" value={title.ownerState} />
                                    <div className="col-span-2">
                                      <DataField label="Address" value={title.ownerAddress} />
                                    </div>
                                    <DataField label="ZIP" value={title.ownerZip} />
                                  </div>
                                </div>
                              )}
                              {/* Lienholders */}
                              <AstsPublicLienholders title={title} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Registration Records — hidden, reserved for future use */}
                  </>
                )}

                {/* Bulk Lookup Mode */}
                {mode === 'bulk' && (
                  <>
                    {/* Bulk Input */}
                    <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-6 mb-6">
                      <BulkAstsPublicInput
                        onSubmit={handleBulkSearch}
                        disabled={isBulkSearching}
                        publicStates={publicStates}
                      />
                    </div>

                    {/* Bulk Loading */}
                    {isBulkSearching && (
                      <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-8 text-center mb-6">
                        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a] mx-auto mb-3" />
                        <p className="text-sm text-[#64748b]">Processing VINs... This may take a minute for public portal lookups.</p>
                      </div>
                    )}

                    {/* Bulk Error */}
                    {bulkError && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{bulkError}</p>
                      </div>
                    )}

                    {/* Bulk Results */}
                    {bulkResults && !isBulkSearching && (
                      <BulkAstsPublicResults results={bulkResults} />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <div className="lg:ml-[240px]">
        <Footer />
      </div>
    </div>
  );
};

export default AstsPublicInquiry;
